import { useCallback, useEffect, useRef, useState } from 'react';
import type { SecretaryPersonaProfile, SecretaryRelationshipProfile } from '../../shared/secretaryPreferences';

/** 服务端按登录身份隔离的两组配置；自由 Soul 独立管理。 */
type Preferences = { persona: SecretaryPersonaProfile; relationship: SecretaryRelationshipProfile };
type PreferenceKind = keyof Preferences;

/** 管理配置草稿与请求生命周期；身份变更撤销旧请求，同步锁阻止重复保存。 */
export function useSecretaryPreferences(listenForSavedChanges = false) {
  const [draft, setDraft] = useState<Preferences | null>(null);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState<'' | 'saved' | 'error'>('');
  const epoch = useRef(0);
  const pending = useRef<object | null>(null);

  /** 读取失败保留已有草稿；仅身份切换会主动清空私人配置。 */
  const refresh = useCallback(async () => {
    if (pending.current) return;
    const operation = {}, generation = epoch.current;
    pending.current = operation; setBusy(true); setMessage('');
    try {
      const read = window.electron.cowork.getSecretaryPreferences;
      if (!read) throw new Error('Preferences unavailable');
      const value = await read();
      if (!value?.persona || !value?.relationship) throw new Error('Invalid preferences');
      if (epoch.current === generation) setDraft(value);
    } catch {
      if (epoch.current === generation) setMessage('error');
    } finally {
      if (pending.current === operation) { pending.current = null; setBusy(false); }
    }
  }, []);

  useEffect(() => {
    /** 切换事件先于新身份生效；此处不可立即请求，以免重新读到旧用户的数据。 */
    const clear = () => { epoch.current++; pending.current = null; setDraft(null); setMessage(''); setBusy(true); };
    const reload = () => { epoch.current++; pending.current = null; void refresh(); };
    clear(); reload();
    window.addEventListener('auth:identityChanging', clear);
    window.addEventListener('auth:stateChanged', reload);
    if (listenForSavedChanges) window.addEventListener('secretary:preferencesChanged', reload);
    return () => {
      epoch.current++; pending.current = null;
      window.removeEventListener('auth:identityChanging', clear);
      window.removeEventListener('auth:stateChanged', reload);
      window.removeEventListener('secretary:preferencesChanged', reload);
    };
  }, [refresh, listenForSavedChanges]);

  /** 更新指定分组草稿，清除上一次保存提示，防止未保存内容仍显示“已保存”。 */
  const update = <K extends PreferenceKind>(kind: K, profile: Preferences[K]) => {
    if (pending.current) return;
    setDraft(value => value ? { ...value, [kind]: profile } : null); setMessage('');
  };

  /** 保存单组快照；请求完成前禁止编辑，服务端规范化结果只替换对应分组。 */
  const save = async (kind: PreferenceKind) => {
    if (!draft || pending.current) return;
    const operation = {}, generation = epoch.current;
    pending.current = operation; setBusy(true); setMessage('');
    try {
      const result = await window.electron.cowork.saveSecretaryPreferences?.(kind, draft[kind]);
      if (!result?.success) throw new Error('Save failed');
      if (epoch.current !== generation) return;
      if (result.profile) setDraft(value => value ? { ...value, [kind]: result.profile } : null);
      setMessage('saved');
      window.dispatchEvent(new Event('secretary:preferencesChanged'));
    } catch {
      if (epoch.current === generation) setMessage('error');
    } finally {
      if (pending.current === operation) { pending.current = null; setBusy(false); }
    }
  };
  return { draft, busy, message, refresh, update, save };
}
